import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, Chip,
    InputAdornment, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel,
    ToggleButton, ToggleButtonGroup, CircularProgress, Grow, Tooltip, Avatar, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';

const ACTSChatBot = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [viewMode, setViewMode] = useState('list');
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
    };

    const markDone = async (e, sessionGuid) => {
        e.stopPropagation();
        try {
            await axios.patch(`${API_URL}/chatbot/sessions/${sessionGuid}/mark-done`, {}, { headers });
            setSessions(prev => prev.map(s =>
                s.cr493_actschatbot_forcepromptingid === sessionGuid
                    ? { ...s, cr493_done: true }
                    : s
            ));
        } catch (err) {
            console.error('Failed to mark done', err);
        }
    };
    const navigate = useNavigate();
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/chatbot/sessions`, { headers });
            setSessions(res.data);
        } catch (err) {
            console.error('Error fetching chatbot sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    const getSessionId = (s) =>
        s.cr493_actschatbot_forcepromptingid || '';

    const getSessionName = (s) =>
        s.cr493_name || s.cr493_title || s.cr493_subject || getSessionId(s) || 'Unnamed Session';

    const getSessionUser = (s) =>
        s.cr493_user || s.cr493_username || s.cr493_createdby || s['_createdby_value@OData.Community.Display.V1.FormattedValue'] || '—';

    const getSessionStatus = (s) =>
        s.cr493_status || s['statecode@OData.Community.Display.V1.FormattedValue'] || (s.statecode === 0 ? 'Active' : s.statecode === 1 ? 'Inactive' : '—');

    const getSessionDate = (s) => {
        const raw = s.createdon || s.cr493_createdon || s.modifiedon;
        if (!raw) return '—';
        return new Date(raw).toLocaleString();
    };

    const getStatusColor = (status) => {
        if (!status) return 'default';
        const s = String(status).toLowerCase();
        if (s === 'active' || s === '0') return 'success';
        if (s === 'inactive' || s === '1') return 'default';
        return 'primary';
    };

    const filteredSessions = sessions.filter(s => {
        // Done filter
        if (filter === 'done' && !(s.cr493_done || s.cr493_isdone)) return false;
        if (filter === 'notdone' && (s.cr493_done || s.cr493_isdone)) return false;

        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (s.cr493_full_name && s.cr493_full_name.toLowerCase().includes(q)) ||
            (s.cr493_email_address && s.cr493_email_address.toLowerCase().includes(q)) ||
            (s.cr493_mainservice && s.cr493_mainservice.toLowerCase().includes(q)) ||
            (s.cr493_main_service && s.cr493_main_service.toLowerCase().includes(q)) ||
            (s.cr493_country && s.cr493_country.toLowerCase().includes(q))
        );
    });

    const handleOpen = (s) => {
        navigate(`/chatbot/${getSessionId(s)}`);
    };

    const sortedSessions = [...filteredSessions].sort((a, b) => {
        if (!sortCol) return 0;
        let av = '', bv = '';
        if (sortCol === 'name') { av = a.cr493_full_name || ''; bv = b.cr493_full_name || ''; }
        else if (sortCol === 'email') { av = a.cr493_email_address || ''; bv = b.cr493_email_address || ''; }
        else if (sortCol === 'mainservice') { av = a.cr493_mainservice || a.cr493_main_service || ''; bv = b.cr493_mainservice || b.cr493_main_service || ''; }
        else if (sortCol === 'subservice') { av = a.cr493_subservice || a.cr493_sub_service || a.cr493_subservices || ''; bv = b.cr493_subservice || b.cr493_sub_service || b.cr493_subservices || ''; }
        else if (sortCol === 'country') { av = a.cr493_country || ''; bv = b.cr493_country || ''; }
        else if (sortCol === 'created') { av = a.createdon || ''; bv = b.createdon || ''; }
        else if (sortCol === 'done') { av = (a.cr493_done || a.cr493_isdone) ? '1' : '0'; bv = (b.cr493_done || b.cr493_isdone) ? '1' : '0'; }
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>

            {/* Gradient Header — same style as Tickets */}
            <Grow in={true} timeout={500}>
                <Box sx={{
                    mb: 4,
                    mt: 2,
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <SmartToyIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            ACTS ChatBot
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Browse ChatBot sessions and open questions from Dataverse
                        </Typography>
                    </Box>
                    <Chip
                        label={`${sessions.length} Sessions`}
                        sx={{ position: 'relative', zIndex: 2, bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, fontSize: '0.9rem', height: 36, px: 1 }}
                    />
                    {/* Decorative circles */}
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </Box>
            </Grow>

            {/* Filter / Search / View Toggle bar */}
            <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 2 }}>

                    {/* Left: status filter chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {[
                            { label: 'All', value: 'all' },
                            { label: 'Done', value: 'done' },
                            { label: 'Not Done', value: 'notdone' },
                        ].map(f => (
                            <Chip
                                key={f.value}
                                label={f.label}
                                onClick={() => setFilter(f.value)}
                                sx={{
                                    fontWeight: '600',
                                    borderRadius: 2,
                                    height: 32,
                                    bgcolor: filter === f.value ? '#1e293b' : 'transparent',
                                    color: filter === f.value ? 'white' : '#64748b',
                                    '&:hover': { bgcolor: filter === f.value ? '#334155' : '#f1f5f9' },
                                    transition: 'all 0.2s ease'
                                }}
                            />
                        ))}
                    </Box>

                    {/* Right: search + view toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
                        <TextField
                            placeholder="Search sessions…"
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                            }}
                            sx={{
                                flexGrow: 1,
                                minWidth: { sm: 250 },
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                                    '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                                }
                            }}
                        />

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => { if (val) setViewMode(val); }}
                            size="small"
                            sx={{
                                height: 40,
                                '& .MuiToggleButton-root': {
                                    color: '#94a3b8',
                                    borderColor: '#e2e8f0',
                                    bgcolor: '#f8fafc',
                                    px: 2,
                                    '&.Mui-selected': { color: '#2563eb', bgcolor: '#eff6ff', borderColor: '#bfdbfe', '&:hover': { bgcolor: '#dbeafe' } },
                                    '&:hover': { bgcolor: '#f1f5f9' }
                                }
                            }}
                        >
                            <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                            <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </Card>

            {/* Content */}
            {loading ? <Loading /> : (
                viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredSessions.length === 0 ? (
                            <Grid item xs={12}>
                                <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No sessions found</Typography>
                            </Grid>
                        ) : filteredSessions.map((s, idx) => (
                            <Grid item xs={12} md={4} lg={3} key={s.cr493_actschatbot_forcepromptingid || idx}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: 4,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        border: '1px solid #f1f5f9',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.1)', borderColor: '#e2e8f0' }
                                    }}
                                    onClick={() => handleOpen(s)}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', mr: 1 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#3b82f6', fontSize: 13, flexShrink: 0 }}>
                                                    {(s.cr493_full_name || s.cr493_name || '?').charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box sx={{ overflow: 'hidden' }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#1e293b', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {s.cr493_full_name || s.cr493_name || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                                        {s.cr493_email_address || '—'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {(s.cr493_done || s.cr493_isdone) ? (
                                                <Chip label="Done" size="small" color="success" sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, flexShrink: 0 }} />
                                            ) : (
                                                <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, flexShrink: 0 }} />
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                            {(s.cr493_mainservice || s.cr493_main_service) && (
                                                <Chip label={s.cr493_mainservice || s.cr493_main_service} size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />
                                            )}
                                            {(s.cr493_subservice || s.cr493_sub_service || s.cr493_subservices) && (
                                                <Chip label={s.cr493_subservice || s.cr493_sub_service || s.cr493_subservices} size="small" color="secondary" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, mb: 2 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, display: 'block' }}>COUNTRY</Typography>
                                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                    {s.cr493_country || '—'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, display: 'block' }}>CREATED</Typography>
                                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                    <AccessTimeIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5, color: '#64748b' }} />
                                                    {getSessionDate(s)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                        <Table sx={{ minWidth: 900 }} aria-label="sessions table">
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={sortCol==='name'} direction={sortCol==='name'?sortDir:'asc'} onClick={()=>handleSort('name')}>Full Name</TableSortLabel></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={sortCol==='email'} direction={sortCol==='email'?sortDir:'asc'} onClick={()=>handleSort('email')}>Email Address</TableSortLabel></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={sortCol==='mainservice'} direction={sortCol==='mainservice'?sortDir:'asc'} onClick={()=>handleSort('mainservice')}>Main Service</TableSortLabel></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={sortCol==='subservice'} direction={sortCol==='subservice'?sortDir:'asc'} onClick={()=>handleSort('subservice')}>Sub Service</TableSortLabel></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={sortCol==='country'} direction={sortCol==='country'?sortDir:'asc'} onClick={()=>handleSort('country')}>Country</TableSortLabel></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={sortCol==='created'} direction={sortCol==='created'?sortDir:'asc'} onClick={()=>handleSort('created')}>Date</TableSortLabel></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center"><TableSortLabel active={sortCol==='done'} direction={sortCol==='done'?sortDir:'asc'} onClick={()=>handleSort('done')}>Done</TableSortLabel></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedSessions.map((s, idx) => (
                                    <TableRow
                                        key={s.cr493_actschatbot_forcepromptingid || idx}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                                        onClick={() => handleOpen(s)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                {idx + 1}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, bgcolor: '#3b82f6', fontSize: 12 }}>
                                                    {(s.cr493_full_name || s.cr493_name || '?').charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {s.cr493_full_name || s.cr493_name || '—'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {s.cr493_email_address || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {s.cr493_mainservice || s.cr493_main_service || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {s.cr493_subservice || s.cr493_sub_service || s.cr493_subservices || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {s.cr493_country || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">{getSessionDate(s)}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {s.cr493_done || s.cr493_isdone ? (
                                                <Chip label="Done" size="small" color="success" sx={{ fontWeight: 600, height: 24, fontSize: '0.72rem' }} />
                                            ) : (
                                                <Tooltip title="Mark as Done">
                                                    <IconButton size="small" color="warning"
                                                        onClick={(e) => markDone(e, s.cr493_actschatbot_forcepromptingid)}
                                                        sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 0.5 }}>
                                                        <CheckCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredSessions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">No sessions found matching your criteria.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            )}
        </Box>
    );
};

export default ACTSChatBot;
